import { useCallback, useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import ValuationSidebarSuperVisor from "./valuationSidebarSuperVisor";
import { useParams } from "react-router-dom";
import PropertyMap from "../map/PropertyMap";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Minus,
  Plus,
  Download,
  UserPlus,
  Upload,
  CheckCircle,
} from "lucide-react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import ValuationVisualization from "../graphics/ValuationVisualization";
import PropertyDashboard from "../graphics/PropertyDashboard";
import PropertyInfographic from "../graphics/PropertyInfographic";
import PropertyValuationDashboard from "../graphics/PropertyValuationDashboard";
import ValuationTimeline from "../graphics/ValuationTimeline";
import { Property } from "../../types/propertyTypes";
import { getAllPropertyImages } from "@/utils/getAllPropertiesImage";
import { generateProfessionalWordReport } from "@/utils/wordReportGenerator";
import { generateProfessionalPdfReport } from "@/utils/pdfReportGenerator";
import TimeTracker from "./PropertyValuationTracker";
import { uploadToCloudinary } from "../../utils/uploadToCloudinary";

interface Collector {
  id: string;
  lastName: string;
  firstName?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  profileImageUrl?: string | null;
}

interface AuthContextType {
  currentUser: {
    name: string;
    role: string;
  };
}

// Add these interfaces
interface ValuationTaskDetails {
  institutionalId: string;
  valuationAssignmentDate: string | Date;
  professionalCert: string;
  principalValuer: string;
  assistantValuer: string;
  regulatoryBody: string;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

// Base URL for API
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

// Default Property object
const defaultProperty: Property = {
  id: "",
  ownerName: "",
  ownerIdNumber: "",
  location: {
    district: "",
    sector: "",
    cell: "",
    village: "",
    province: "",
    region: "",
    subRegion: "",
    county: "",
    subCounty: "",
    parish: "",
    streetAddress: "",
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
  },
  upi: "",
  landParcel: {
    area: 0,
    landUse: "",
    slopeCategory: "",
    accessToRoad: false,
  },
  mainBuilding: {
    constructedArea: 0,
    constructionYear: 0,
    numberOfFloors: 0,
    constructionQuality: "",
    exteriorFinish: "",
    roofType: "",
    maintenanceCondition: "",
  },
  amenities: {
    hasElectricity: false,
    hasWater: false,
    hasSewage: false,
    hasInternet: false,
    hasFence: false,
    hasGarden: false,
    hasParkingSpace: false,
  },
  buildingComponents: {},
  adjustmentsAndVariables: {},
  buildingDescription: {},
  propertyDetails: {},
  propertyPhotos: [],
  valuationDetails: {
    status: "Pending",
    landValue: 0,
    buildingValue: 0,
    amenitiesValue: 0,
    fenceLength: 0,
    verandahRate: 0,
    verandahArea: 0,
    entranceGateRate: 0,
    entranceGateLength: 0,
    depreciationValue: 0,
    totalPropertyValue: 0,
    valuationDate: "",
    collectorNotes: "",
    approvedBy: null,
    approvedDate: null,
    assignedCollector: "",
  },
};

// Helper function to format dates for input fields
const formatDateForInput = (
  dateString: string | Date | undefined | null,
): string => {
  if (!dateString) return "";

  try {
    const date =
      typeof dateString === "string" ? new Date(dateString) : dateString;

    // Format as YYYY-MM-DD for date input
    const formatted = date.toISOString().split("T")[0];
    return formatted || "";
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Helper function to get all property images

// PropertyDetails Component
interface PropertyDetailsProps {
  supervisorMode?: boolean;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  supervisorMode = false,
}) => {
  const { id: propertyId } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  const [property, setProperty] = useState<Property>(defaultProperty);
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { logout, token: authToken } = useAuth();
  const [isApproving, setIsApproving] = useState(false);
  const [isCollectorModalOpen, setIsCollectorModalOpen] = useState(false);
  const [selectedCollector, setSelectedCollector] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [zoomImage, setZoomImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 4;

  // Image gallery states
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [filter, setFilter] = useState<"data" | "graphics">("data");
  const { theme, setTheme } = useTheme();

  // Valuation task details state
  const [valuationTaskDetails, setValuationTaskDetails] =
    useState<ValuationTaskDetails>({
      institutionalId: "",
      valuationAssignmentDate: new Date(),
      professionalCert: "",
      principalValuer: "",
      assistantValuer: "",
      regulatoryBody: "",
    });

  const [institutionalClients, setInstitutionalClients] = useState<User[]>([]);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isClientsLoaded, setIsClientsLoaded] = useState(false);

  // Get all property images
  const propertyImages = getAllPropertyImages(property);
  const hasImages = propertyImages.length > 0;

  // Navigation functions
  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // Image gallery handlers
  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % propertyImages.length);
    setZoomLevel(1);
  };

  const prevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + propertyImages.length) % propertyImages.length,
    );
    setZoomLevel(1);
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
    setZoomLevel(1);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  // Fetch property data from API
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) return;
      setIsLoading(true);
      setErrorMessage("");
      try {
        const Valuation_token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property: ${response.statusText}`);
        }

        const result = await response.json();
        setProperty(result.data);

        // Update valuation task details when property loads
        if (result.data.valuationTaskDetails) {
          setValuationTaskDetails(result.data.valuationTaskDetails);
        }
      } catch (error) {
        console.error("Error fetching property:", error);
        setErrorMessage("Failed to load property details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, isAssigning]);

  // Fetch collectors from API
  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const response = await fetch(`${BASE_URL}/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch collectors: ${response.statusText}`);
        }

        const result = await response.json();
        const users: User[] = result.data || [];
        const dataCollectors = users.filter(
          (user) => user.role === "DATA_COLLECTOR",
        );
        const collectorArray: Collector[] = dataCollectors.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
        }));
        setCollectors(collectorArray);
      } catch (error) {
        console.error("Error fetching collectors:", error);
      }
    };
    fetchCollectors();
  }, []);

  // Fetch institutional clients
  useEffect(() => {
    const fetchInstitutionalClients = async () => {
      try {
        const response = await fetch(`${BASE_URL}/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch users");
        const result = await response.json();
        const users: User[] = result.data || [];
        const clients = users.filter(
          (user) => user.role === "INSTITUTIONAL_CLIENT",
        );
        setInstitutionalClients(clients);
        setIsClientsLoaded(true);
      } catch (error) {
        console.error("Error fetching institutional clients:", error);
        setIsClientsLoaded(true);
      }
    };
    fetchInstitutionalClients();
  }, []);
  const isUganda =
    !!property?.location?.region ||
    (property as any)?.location?.country === "UG";
  const currency = isUganda ? "UGX" : "RWF";
  // Update valuation task details when property loads
  useEffect(() => {
    if (property.valuationTaskDetails) {
      // Ensure dates are properly converted
      const taskDetails = { ...property.valuationTaskDetails };

      // Convert string dates to Date objects if needed
      if (
        taskDetails.valuationAssignmentDate &&
        typeof taskDetails.valuationAssignmentDate === "string"
      ) {
        taskDetails.valuationAssignmentDate = new Date(
          taskDetails.valuationAssignmentDate,
        );
      }

      setValuationTaskDetails(taskDetails);
    }
  }, [property.valuationTaskDetails]);

  // Filtered collectors based on search query
  const filteredCollectors = collectors.filter((collector) =>
    collector.lastName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Filtered clients based on search query
  const filteredClients = institutionalClients.filter((client) =>
    `${client.firstName} ${client.lastName}`
      .toLowerCase()
      .includes(clientSearchQuery.toLowerCase()),
  );

  /* ================= UPLOAD REPORT ================= */
  const token = localStorage.getItem("valuation_token");
  const handleUploadReport = async () => {
    if (!token) {
      setErrorMessage("Authentication token missing. Please log in again.");
      return;
    }

    if (!selectedFile) {
      setErrorMessage("Please select a PDF file");
      return;
    }

    if (!propertyId) {
      setErrorMessage("Property ID is missing");
      return;
    }

    try {
      setUploading(true);
      setErrorMessage("");

      // Upload PDF to Cloudinary
      const pdfUrl = await uploadToCloudinary(selectedFile);

      // Create report with the uploaded PDF
      const payload = {
        propertyId: propertyId,
        pdfUrl,
      };

      const res = await fetch(`${BASE_URL}/reports`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to upload report");
      }

      const result = await res.json();
      console.log("Report uploaded successfully:", result);

      setShowUploadModal(false);
      setSelectedFile(null);
      setSuccessMessage("Report uploaded successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Upload error:", error);
      setErrorMessage(
        `Failed to upload report: ${error.message || "Unknown error"}`,
      );
    } finally {
      setUploading(false);
    }
  };

  /* ================= FILE HANDLING ================= */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setErrorMessage("Please select a PDF file");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("File size should be less than 10MB");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setSelectedFile(file);
  };
  // Handler for generate report button
  const handleGenerateReport = async () => {
    if (!property || !property.id) {
      alert("No property data available to generate report.");
      return;
    }
    
    // Collect all required data for the professional report
    const reportData = {
      // Institutional Client Information (using actual data from valuationTaskDetails)
      institutionalClient: valuationTaskDetails.institutionalId
        ? institutionalClients.find(
            (c) => c.id === valuationTaskDetails.institutionalId,
          )
          ? `${institutionalClients.find((c) => c.id === valuationTaskDetails.institutionalId)?.firstName} ${institutionalClients.find((c) => c.id === valuationTaskDetails.institutionalId)?.lastName}`
          : "Institutional Client"
        : "Not Specified",

      clientPobox: `P.O. BOX ${property.location?.district || "Kigali"}`,

      dateOfAssignment: valuationTaskDetails.valuationAssignmentDate
        ? new Date(
            valuationTaskDetails.valuationAssignmentDate,
          ).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          }),

      // Property Owner Information
      clientNames: property.ownerName || "N/A",
      clientIdNumber: property.ownerIdNumber || "N/A",
      clientPhone: property.phone || "+250 785182823",

      // Valuation Team Information (using actual valuationTaskDetails)
      valuationFirm: "Rwanda Property Valuation Services Ltd",
      certifiedValuer:
        valuationTaskDetails.professionalCert || "Professional Certification",
      principalCertifiedValuer:
        valuationTaskDetails.principalValuer || "Arsene Mizero",
      assistantValuerName:
        valuationTaskDetails.assistantValuer || "IZERE Felix",
      regulatoryBody: valuationTaskDetails.regulatoryBody || "IRPV",

      // Dates
      dateOfInspection: property.valuationDetails?.valuationDate
        ? new Date(property.valuationDetails.valuationDate).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "long",
              year: "numeric",
            },
          )
        : new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          }),

      // Property Identification
      propertyIdentification: {
        upiNumber: property.upi || "4/01/16/05/7676",
        plotNumber: property.plotNumber || "7676",
        location: `${property.location?.village || "Bweranyange"}, ${property.location?.cell || "Nyamiyaga"}, ${property.location?.sector || "Murambi"}, ${property.location?.district || "Gatsibo"}`,
        coordinates: property.location?.coordinates
          ? `${property.location.coordinates.latitude}, ${property.location.coordinates.longitude}`
          : "-1.9897635, 30.0397767",
        streetAddress: property.location?.streetAddress || "KG 324 Street",
        province: property.location?.province || "East",
      },

      // Zoning Description (using actual zoning data)
      zoningDescription: `The ${property.zoning || "R1B-Rural residential zone"} in ${
        property.location?.district || "Gatsibo"
      } District is a ${property.landParcel?.landUse?.toLowerCase() || "residential"} district offering ${
        property.mainBuilding?.numberOfFloors > 1
          ? "multi-storey"
          : "single-storey"
      } developments. The zoning regulations allow for ${
        property.landParcel?.landUse === "Residential"
          ? "residential dwellings with complementary public facilities"
          : property.landParcel?.landUse === "Commercial"
            ? "commercial and business activities"
            : "mixed-use development"
      } as part of the ${property.location?.district?.includes("Rural") ? "rural community" : "urban fabric"}.`,

      // Services Description (using actual amenitiesData)
      servicesDescription: `Main electricity ${property.amenitiesData?.hasElectricity ? "is" : "is not"} connected to the subject property. Water supply ${property.amenitiesData?.hasWater ? "is available" : "is not connected"}. ${
        property.landParcel?.accessToRoad
          ? "There is direct access to the property via the main road"
          : "Access to the property is limited"
      }. ${
        property.valuationDetails?.fenceLength > 0
          ? `The property features ${property.valuationDetails.fenceLength}m of perimeter fencing.`
          : "The property has no perimeter fencing."
      } Access is via ${
        property.location?.streetAddress?.toLowerCase().includes("tarmac")
          ? "tarmac road"
          : property.valuationDetails?.pavedArea > 0
            ? "paved driveway"
            : "murram road"
      }.`,

      // Plot Description (using all land parcel data)
      plotDescription: `The plot is located at ${property.upi || "4/01/16/05/7676"} covering an area of ${
        property.landParcel?.area || 1498
      } m² on a ${property.landParcel?.slopeCategory?.toLowerCase() || "gentle"} slope. The land use is classified as ${
        property.landParcel?.landUse || "Residential"
      }. ${
        property.valuationDetails?.fenceLength > 0
          ? `The property is secured by ${property.valuationDetails.fenceLength}m of fencing with ${property.valuationDetails?.entranceGateLength || 0}m entrance gate.`
          : "The property has no perimeter fencing."
      } ${
        property.amenitiesData?.hasGarden
          ? `There is a garden area of ${property.valuationDetails?.gardenArea || 45}m².`
          : ""
      } ${
        property.valuationDetails?.pavedArea > 0
          ? `The property includes ${property.valuationDetails.pavedArea}m² of paved area.`
          : ""
      }`,

      // Improvements Description (using building components and main building data)
      improvementsDescription: `The plot is developed with a ${
        property.mainBuilding?.constructionQuality || "standard"
      } quality ${
        property.mainBuilding?.numberOfFloors > 1
          ? "multi-storey"
          : "single-storey"
      } ${property.landParcel?.landUse?.toLowerCase() || "residential"} building with a constructed area of ${
        property.mainBuilding?.constructedArea || 6
      } m². The main house area is ${property.valuationDetails?.mainHouseArea || 3278}m²${
        property.valuationDetails?.annexHouseArea > 0
          ? ` with an annex building of ${property.valuationDetails.annexHouseArea}m²`
          : ""
      }. The building features ${
        property.mainBuilding?.bedrooms || 7
      } bedrooms, ${property.mainBuilding?.bathrooms || 11} bathrooms${
        property.valuationDetails?.verandahArea > 0
          ? `, and a verandah area of ${property.valuationDetails.verandahArea}m²`
          : ""
      }. The construction features a ${property.mainBuilding?.roofType?.toLowerCase() || "concrete"} roof and is in ${
        property.mainBuilding?.maintenanceCondition >= 0
          ? "good"
          : property.mainBuilding?.maintenanceCondition === -2
            ? "poor"
            : "average"
      } condition.`,

      // Additional Components (using buildingComponents)
      additionalComponents: {
        outsideKitchen:
          property.buildingComponents?.outsideKitchenDescription || "retf",
        waterTank: property.buildingComponents?.waterTankDescription || "hgfh",
        landscaping:
          property.buildingComponents?.landscapingDescription || "gfh",
        soilDrainage:
          property.buildingComponents?.soilDrainageDescription || "gfjh",
        pvcPipes: property.buildingComponents?.pvcPipesDescription || "gfjnj",
        washTaps: property.buildingComponents?.washTapsDescription || "nghn",
        fixturesFittings:
          property.buildingComponents?.fixturesFittingsDescription || "fghfg",
      },

      // Building Condition Assessment
      buildingCondition: {
        floor: property.mainBuilding?.floorCondition || "Poor",
        walls: property.mainBuilding?.wallsCondition || "Average",
        windows: property.mainBuilding?.windowsCondition || "Good",
        doors: property.mainBuilding?.doorsCondition || "Good",
        roof: property.mainBuilding?.roofCondition || "Average",
        overall:
          property.mainBuilding?.maintenanceCondition >= 0
            ? "Good"
            : property.mainBuilding?.maintenanceCondition === -2
              ? "Poor"
              : "Average",
      },

      // Remarks
      remarks: `The subject property is located in ${
        property.location?.district || "Gatsibo"
      } District, which is ${
        property.location?.district?.includes("Rural") ? "a rural" : "an urban"
      } area. The neighborhood comprises ${
        property.mainBuilding?.maintenanceCondition >= 0
          ? "well-maintained properties"
          : "properties in varying conditions"
      }. The subject property itself is in ${
        property.mainBuilding?.maintenanceCondition >= 0
          ? "good condition"
          : property.mainBuilding?.maintenanceCondition === -2
            ? "poor condition requiring maintenance"
            : "average condition"
      }. The construction uses ${
        property.mainBuilding?.constructionQuality
          ? `${property.mainBuilding.constructionQuality.toLowerCase()} quality materials`
          : "standard construction materials"
      } with ${
        property.amenitiesData?.hasElectricity &&
        property.amenitiesData?.hasWater
          ? "comprehensive utility connections"
          : "basic services"
      }.`,

      // Official Information
      landRegistrationOfficer: "Land Registration Office",
      approvedBy: property.valuationDetails?.approvedBy || "Supervisor Name",
      approvedDate: property.valuationDetails?.approvedDate
        ? new Date(property.valuationDetails.approvedDate).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "long",
              year: "numeric",
            },
          )
        : "N/A",

      // Financial Values (using actual valuationDetails)
      openMarketValue:
        property.valuationDetails?.openMarketValue ||
        property.valuationDetails?.totalPropertyValue ||
        5208633,
      insuranceValue: property.valuationDetails?.insuranceValue || 4166915.1,
      forcedSaleValue: property.valuationDetails?.forcedSaleValue || 5208633,
      landValue: property.valuationDetails?.landValue || 5208546,
      buildingValue: property.valuationDetails?.buildingValue || 1639000,
      amenitiesValue: property.valuationDetails?.amenitiesValue || 10000,
      improvementsValue: property.valuationDetails?.improvementsValue || 87,
      depreciationValue: property.valuationDetails?.depreciationValue || 0,
      totalPropertyValue: property.valuationDetails?.totalPropertyValue || 0,

      // Map Photos (using actual coordinates)
      mapPhotos: property.location?.coordinates
        ? [
            `https://maps.googleapis.com/maps/api/staticmap?center=${property.location.coordinates.latitude},${property.location.coordinates.longitude}&zoom=15&size=600x400&markers=color:blue%7C${property.location.coordinates.latitude},${property.location.coordinates.longitude}&key=YOUR_API_KEY`,
            `https://maps.googleapis.com/maps/api/staticmap?center=${property.location.coordinates.latitude},${property.location.coordinates.longitude}&zoom=13&size=600x400&maptype=roadmap&key=YOUR_API_KEY`,
            `https://maps.googleapis.com/maps/api/staticmap?center=${property.location.coordinates.latitude},${property.location.coordinates.longitude}&zoom=12&size=600x400&maptype=terrain&key=YOUR_API_KEY`,
          ]
        : [
            "https://maps.googleapis.com/maps/api/staticmap?center=-1.9897635,30.0397767&zoom=15&size=600x400&markers=color:blue%7C-1.9897635,30.0397767&key=YOUR_API_KEY",
            "https://maps.googleapis.com/maps/api/staticmap?center=-1.9897635,30.0397767&zoom=13&size=600x400&maptype=roadmap&key=YOUR_API_KEY",
          ],

      // Property Photos (using actual propertyPhotos array)
      propertyPhotos: property.propertyPhotos || [],

      // Comprehensive Valuation Parameters
      valuationParameters: [
        {
          parameter: "Land Area",
          value: `${property.landParcel?.area || 1498} m²`,
          rate: `${property.valuationDetails?.landRate || 3477}/m²`,
          total: `${property.valuationDetails?.landValue?.toLocaleString()}`,
        },
        {
          parameter: "Main House Area",
          value: `${property.valuationDetails?.mainHouseArea || 3278} m²`,
          rate: `${property.valuationDetails?.mainHouseRate || 3080}/m²`,
          total: `${(property.valuationDetails?.mainHouseArea * property.valuationDetails?.mainHouseRate)?.toLocaleString()}`,
        },
        {
          parameter: "Annex Building Area",
          value: `${property.valuationDetails?.annexHouseArea || 34} m²`,
          rate: `${property.valuationDetails?.annexRate || 3}/m²`,
          total: `${(property.valuationDetails?.annexHouseArea * property.valuationDetails?.annexRate)?.toLocaleString()}`,
        },
        {
          parameter: "Building Value",
          value: "Total Structure",
          rate: "Calculated",
          total: `${property.valuationDetails?.buildingValue?.toLocaleString()}`,
        },
        {
          parameter: "Fencing",
          value: `${property.valuationDetails?.fenceLength || 53} m`,
          rate: `${property.valuationDetails?.fenceRate || 43}/m`,
          total: `${(property.valuationDetails?.fenceLength * property.valuationDetails?.fenceRate)?.toLocaleString()}`,
        },
        {
          parameter: "Entrance Gate",
          value: `${property.valuationDetails?.entranceGateLength || 88} m`,
          rate: `${property.valuationDetails?.entranceGateRate || 99}/m`,
          total: `${(property.valuationDetails?.entranceGateLength * property.valuationDetails?.entranceGateRate)?.toLocaleString()}`,
        },
        {
          parameter: "Retaining Walls",
          value: `${property.valuationDetails?.retainingWallsLength || 42} m`,
          rate: `${property.valuationDetails?.retainingWallRate || 78}/m`,
          total: `${(property.valuationDetails?.retainingWallsLength * property.valuationDetails?.retainingWallRate)?.toLocaleString()}`,
        },
        {
          parameter: "Verandah Area",
          value: `${property.valuationDetails?.verandahArea || 44} m²`,
          rate: `${property.valuationDetails?.verandahRate || 12}/m²`,
          total: `${(property.valuationDetails?.verandahArea * property.valuationDetails?.verandahRate)?.toLocaleString()}`,
        },
        {
          parameter: "Garden Area",
          value: `${property.valuationDetails?.gardenArea || 45} m²`,
          rate: `${property.valuationDetails?.gardenRate || 97}/m²`,
          total: `${(property.valuationDetails?.gardenArea * property.valuationDetails?.gardenRate)?.toLocaleString()}`,
        },
        {
          parameter: "Paved Area",
          value: `${property.valuationDetails?.pavedArea || 12} m²`,
          rate: `${property.valuationDetails?.pavedAreaRate || 55}/m²`,
          total: `${(property.valuationDetails?.pavedArea * property.valuationDetails?.pavedAreaRate)?.toLocaleString()}`,
        },
        {
          parameter: "Septic Tanks",
          value: `${property.valuationDetails?.septicTankNumber || 5} units`,
          rate: `${property.valuationDetails?.septicTankValue || 87}/unit`,
          total: `${(property.valuationDetails?.septicTankNumber * property.valuationDetails?.septicTankValue)?.toLocaleString()}`,
        },
        {
          parameter: "Landscaping",
          value: "Landscaping Works",
          rate: "Lump Sum",
          total: `${property.valuationDetails?.landscapingValue?.toLocaleString()}`,
        },
        {
          parameter: "Amenities Value",
          value: "Additional Facilities",
          rate: "Calculated",
          total: `${property.valuationDetails?.amenitiesValue?.toLocaleString()}`,
        },
        {
          parameter: "Improvements Value",
          value: "Enhancements",
          rate: "Calculated",
          total: `${property.valuationDetails?.improvementsValue?.toLocaleString()}`,
        },
        {
          parameter: "Depreciation",
          value: `${property.valuationDetails?.depreciationRate || 1}%`,
          rate: "Annual",
          total: `${property.valuationDetails?.depreciationValue?.toLocaleString()}`,
        },
        {
          parameter: "Total Property Value",
          value: "Final Valuation",
          rate: "Sum Total",
          total: `${property.valuationDetails?.totalPropertyValue?.toLocaleString() || property.valuationDetails?.openMarketValue?.toLocaleString()}`,
        },
      ],

      // Valuation Summary
      valuationSummary: {
        landValue: property.valuationDetails?.landValue || 5208546,
        buildingValue: property.valuationDetails?.buildingValue || 1639000,
        amenitiesValue: property.valuationDetails?.amenitiesValue || 10000,
        improvementsValue: property.valuationDetails?.improvementsValue || 87,
        depreciation: property.valuationDetails?.depreciationValue || 0,
        totalValue:
          property.valuationDetails?.totalPropertyValue ||
          property.valuationDetails?.openMarketValue ||
          5208633,
        openMarketValue: property.valuationDetails?.openMarketValue || 5208633,
        insuranceValue: property.valuationDetails?.insuranceValue || 4166915.1,
        forcedSaleValue: property.valuationDetails?.forcedSaleValue || 5208633,
        forcedSaleDiscount: `${property.valuationDetails?.forcedSaleDiscount || 0}%`,
        maintenanceCoefficient:
          property.valuationDetails?.maintenanceCoefficient || 3,
        annualDepreciationRate: `${property.valuationDetails?.annualDepreciationRate || 3}%`,
      },

      // Property Characteristics
      propertyCharacteristics: {
        constructionYear: property.mainBuilding?.constructionYear || 0,
        effectiveAge: property.effectiveAge || 3,
        numberOfFloors: property.mainBuilding?.numberOfFloors || 0,
        bedrooms: property.mainBuilding?.bedrooms || 7,
        bathrooms: property.mainBuilding?.bathrooms || 11,
        roofType: property.mainBuilding?.roofType || "Concrete",
        exteriorFinish:
          property.mainBuilding?.exteriorFinish || "Not specified",
        constructionQuality:
          property.mainBuilding?.constructionQuality || "Standard",
        maintenanceCondition:
          property.mainBuilding?.maintenanceCondition >= 0
            ? "Good"
            : property.mainBuilding?.maintenanceCondition === -2
              ? "Poor"
              : "Average",
        slopeCategory: property.landParcel?.slopeCategory || "Gentle",
        landUse: property.landParcel?.landUse || "Residential",
        accessToRoad: property.landParcel?.accessToRoad ? "Yes" : "No",
        zoning: property.zoning || "R1B-Rural residential zone",
      },

      // Amenities Checklist
      amenitiesChecklist: {
        electricity: property.amenitiesData?.hasElectricity
          ? "Available"
          : "Not Available",
        water: property.amenitiesData?.hasWater ? "Available" : "Not Available",
        garden: property.amenitiesData?.hasGarden ? "Yes" : "No",
        annexBuilding: property.amenitiesData?.hasAnnexBuilding ? "Yes" : "No",
        outsideKitchen: property.amenitiesData?.hasOutsideKitchen
          ? "Yes"
          : "No",
        pavedParking: property.amenitiesData?.hasPavedParking ? "Yes" : "No",
        accessToRoad: property.amenitiesData?.accessToRoad ? "Yes" : "No",
      },

      // Report Metadata
      reportMetadata: {
        reportGenerated: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
        propertyId: property.id || "f8b96bc5-eab2-4844-a843-95b040973c7c",
        valuationStatus: property.valuationDetails?.status || "New",
        collectorAssigned:
          property.valuationDetails?.assignedCollector || "Alice Collector",
        createdAt: property.createdAt
          ? new Date(property.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })
          : "N/A",
        lastUpdated: property.updatedAt
          ? new Date(property.updatedAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })
          : "N/A",
      },
    };
    console.log("reportData:~-", reportData);

    setIsGeneratingPDF(true);
    try {
      await generateProfessionalPdfReport(property, reportData);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Handler for task detail changes
  const handleTaskDetailChange = (
    field: keyof ValuationTaskDetails,
    value: string | Date,
  ) => {
    setValuationTaskDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const requiredFields: (keyof ValuationTaskDetails)[] = [
      "institutionalId",
      "valuationAssignmentDate",
      "professionalCert",
      "principalValuer",
      "regulatoryBody",
    ];

    requiredFields.forEach((field) => {
      if (!valuationTaskDetails[field]?.toString().trim()) {
        const fieldNames: Record<string, string> = {
          institutionalId: "Institutional Client",
          valuationAssignmentDate: "Date of Valuation/Assignment",
          professionalCert: "Professional Certification",
          principalValuer: "Principal Valuer",
          regulatoryBody: "Regulatory Body",
        };
        errors[field] = `${fieldNames[field]} is required`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save valuation details
  const handleSaveValuationDetails = async () => {
    console.log("Saving valuationTaskDetails:", valuationTaskDetails);
    if (!validateForm()) {
      setErrorMessage("Please fill in all required fields");
      return;
    }

    if (!propertyId) {
      setErrorMessage("Property ID is missing");
      return;
    }

    setIsSavingDetails(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      console.log("Saving valuationTaskDetails:", valuationTaskDetails);

      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: valuationTaskDetails,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to save valuation details",
        );
      }

      const result = await response.json();

      // Update local property state
      setProperty((prev) => ({
        ...prev,
        valuationTaskDetails: result.data.valuationTaskDetails,
      }));

      setSuccessMessage("Valuation task details saved successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving valuation details:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to save valuation details",
      );
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Handle opening the collector modal
  const handleOpenCollectorModal = () => {
    setIsCollectorModalOpen(true);
    setSearchQuery("");
  };

  // Handle closing the collector modal
  const handleCloseCollectorModal = () => {
    setIsCollectorModalOpen(false);
    setSelectedCollector("");
    setSearchQuery("");
  };

  // Handle assigning a collector
  const handleAssignCollector = async () => {
    if (!selectedCollector || !propertyId) return;
    setIsAssigning(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      const collector = collectors.find((c) => c.id === selectedCollector);
      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            assignedCollector: collector
              ? `${collector.firstName} ${collector.lastName}`
              : "",
            collectorId: selectedCollector,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to assign collector");
      }

      const result = await response.json();
      console.log("Assign collector result:", result);
      setProperty(result.data);
      setSuccessMessage("Collector assigned successfully!");
      setIsCollectorModalOpen(false);
      setSelectedCollector("");
      setSearchQuery("");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error assigning collector:", error);
      setErrorMessage("Failed to assign collector. Please try again.");
      setIsCollectorModalOpen(false);
    } finally {
      setIsAssigning(false);
      setIsCollectorModalOpen(false);
    }
  };

  // Handle supervisor approval
  const handleApprove = async () => {
    if (!currentUser || !propertyId) return;
    setIsApproving(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");

      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            status: "Completed",
            approvedBy: currentUser.name,
            approvedDate: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to approve valuation");
      }

      const result = await response.json();

      console.log("result, result:----", result);
      console.log("Approval result:", result);
      setProperty(result.data);
      setSuccessMessage("Valuation approved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error approving valuation:", error);
      setErrorMessage("Failed to approve valuation. Please try again.");
    } finally {
      setIsApproving(false);
    }
  };

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        <Header theme={theme} setTheme={setTheme} logout={logout} />

        <div className="flex">
          <div className="w-64 min-h-screen">
            <Sidebar
              onViewData={() => handleAction("view-data")}
              onViewReport={() => handleAction("view-report")}
            />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 text-center py-20 w-full mt-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-600">Loading property details...</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="">
      <Header theme={theme} setTheme={setTheme} logout={logout} />

      <div className="flex">
        <div className="w-64 min-h-screen">
          <Sidebar
            onViewData={() => handleAction("view-data")}
            onViewReport={() => handleAction("view-report")}
          />
        </div>

        <div className="flex-1 ml-4 space-y-6 pl-6 bg-gray-50 min-h-screen">
          <div>
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-lg font-semibold text-gray-800">
                  Property Details
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  UPI: {property?.upi || "Not assigned"}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="my-1 mx-5">
                <div className="inline-flex items-center bg-white/40 backdrop-blur-lg p-1 rounded-2xl shadow-xl border border-white/30">
                  <button
                    onClick={() => setFilter("data")}
                    className={`px-7 py-3.5 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                      filter === "data"
                        ? "bg-white/90 text-indigo-700 shadow-lg border border-white"
                        : "text-gray-700 hover:bg-white/60 hover:shadow-md"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z" />
                        <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z" />
                        <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z" />
                      </svg>
                      Data
                    </span>
                  </button>
                  <button
                    onClick={() => setFilter("graphics")}
                    className={`px-7 py-3.5 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                      filter === "graphics"
                        ? "bg-white/90 text-indigo-700 shadow-lg border border-white"
                        : "text-gray-700 hover:bg-white/60 hover:shadow-md"
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Visuals
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex space-x-1 mr-3">
                  {/* Generate Report Button */}
                  <button
                    onClick={handleGenerateReport}
                    disabled={isGeneratingPDF || !property}
                    className="inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingPDF ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-1.5" />
                        Generate Report
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center px-2 py-2 mx-2 border border-gray-300 text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Upload className="h-4 w-4 " />
                    Upload Report
                  </button>

                  {showUploadModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4">
                      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            Upload Valuation Report
                          </h3>
                          <button
                            onClick={() => {
                              setShowUploadModal(false);
                              setSelectedFile(null);
                              setErrorMessage("");
                            }}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select PDF File (max 10MB)
                          </label>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex flex-col items-center justify-center text-gray-500">
                                <Upload className="h-8 w-8 mb-2" />
                                <span className="text-sm">
                                  {selectedFile
                                    ? selectedFile.name
                                    : "Click to select PDF"}
                                </span>
                                <span className="text-xs mt-1">
                                  PDF files only
                                </span>
                              </div>
                            </button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf"
                              onChange={handleFileSelect}
                              className="hidden"
                            />
                          </div>
                        </div>

                        {errorMessage && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-red-600 text-sm">
                              {errorMessage}
                            </p>
                          </div>
                        )}

                        {selectedFile && (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                            <p className="text-green-600 text-sm">
                              Selected: {selectedFile.name} (
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                          </div>
                        )}

                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => {
                              setShowUploadModal(false);
                              setSelectedFile(null);
                              setErrorMessage("");
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUploadReport}
                            disabled={uploading || !selectedFile}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {uploading ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Uploading...
                              </>
                            ) : (
                              "Upload Report"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    className="inline-flex items-center px-2 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    onClick={handleOpenCollectorModal}
                    disabled={isAssigning}
                  >
                    {isAssigning ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Assigning...
                      </>
                    ) : (
                      "Assign Collector"
                    )}
                  </button>
                  {/* Collector Assignment Modal */}
                  {isCollectorModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4">
                      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            Assign Collector
                          </h3>
                          <button
                            onClick={handleCloseCollectorModal}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <svg
                              className="h-6 w-6"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search Collector
                          </label>
                          <input
                            type="text"
                            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Search by name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                        <div className="mb-4 max-h-64 overflow-y-auto">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Collector
                          </label>
                          {filteredCollectors.length > 0 ? (
                            <ul className="border border-gray-300 rounded-md divide-y divide-gray-200">
                              {filteredCollectors.map((collector) => (
                                <li
                                  key={collector.id}
                                  className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                                    selectedCollector === collector.id
                                      ? "bg-indigo-100"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setSelectedCollector(collector.id)
                                  }
                                >
                                  {collector.firstName} {collector.lastName}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500">
                              No collectors found
                            </p>
                          )}
                        </div>
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={handleCloseCollectorModal}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAssignCollector}
                            disabled={!selectedCollector || isAssigning}
                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            {isAssigning ? "Assigning..." : "Assign"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    className="inline-flex items-center px-1 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                    onClick={handleApprove}
                  >
                    {isApproving ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Approving...
                      </>
                    ) : (
                      "Approve Valuation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filter === "data" && (
            <div className="flex space-x-3 mr-5">
              {/* Property Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="col-span-3 lg:col-span-2">
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    {/* Property Header with Image */}
                    <div className="flex flex-col md:flex-row justify-between items-start mb-6">
                      <div className="mb-4 md:mb-0">
                        <h2 className="text-xl font-medium text-gray-800">
                          {property?.ownerName || "Unnamed Property"}
                        </h2>
                        <div className="flex items-center mt-2 space-x-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              property.valuationDetails?.status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : property.valuationDetails?.status ===
                                    "Pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {property.valuationDetails?.status || "New"}
                          </span>
                          {property.valuationDetails?.assignedCollector && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Assigned to:{" "}
                              {property.valuationDetails.assignedCollector}
                            </span>
                          )}
                          {valuationTaskDetails.institutionalId && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {isClientsLoaded &&
                              institutionalClients.find(
                                (c) =>
                                  c.id === valuationTaskDetails.institutionalId,
                              )
                                ? `Institutional Client: ${
                                    institutionalClients.find(
                                      (c) =>
                                        c.id ===
                                        valuationTaskDetails.institutionalId,
                                    )?.firstName
                                  } ${
                                    institutionalClients.find(
                                      (c) =>
                                        c.id ===
                                        valuationTaskDetails.institutionalId,
                                    )?.lastName
                                  }`
                                : "Saved ✓"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Main Property Image with Click to Open Lightbox */}
                      {hasImages && propertyImages[0] ? (
                        <div className="relative">
                          <button
                            onClick={() => openLightbox(0)}
                            className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 group relative focus:outline-none z-10"
                          >
                            <img
                              src={propertyImages[0]?.url || ""}
                              alt={propertyImages[0]?.label || "Property image"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                              <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                View All Images
                              </span>
                            </div>
                          </button>
                          <div className="text-xs text-gray-500 mt-1 text-center">
                            {propertyImages.length} images available
                          </div>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                          <div className="text-center">
                            <div className="text-2xl mb-1">📷</div>
                            <span className="text-xs">No Images</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Image Gallery Sidebar - Always visible on Page 1 */}
                    {hasImages && currentPage === 1 && (
                      <div className="mb-6 pb-6 border-b border-gray-200">
                        <h3 className="text-base font-medium text-gray-800 mb-3">
                          Property Images
                        </h3>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                          {propertyImages.slice(0, 16).map((image, index) => (
                            <button
                              key={index}
                              onClick={() => openLightbox(index)}
                              className={`aspect-square rounded overflow-hidden border-2 transition-all hover:border-blue-400 ${
                                index === selectedImageIndex
                                  ? "border-blue-500"
                                  : "border-gray-200"
                              }`}
                            >
                              <img
                                src={image.url}
                                alt={image.label}
                                className="w-full h-full object-cover hover:scale-110 transition-transform duration-200"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src =
                                    "https://via.placeholder.com/100/cccccc/969696?text=Image";
                                }}
                              />
                            </button>
                          ))}
                          {propertyImages.length > 16 && (
                            <button
                              onClick={() => openLightbox(0)}
                              className="aspect-square rounded bg-gray-50 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-blue-300 hover:text-blue-500 transition-colors"
                            >
                              <span className="text-xl">+</span>
                              <span className="text-xs mt-1">
                                {propertyImages.length - 16} more
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Page Content */}
                    <div className="min-h-[400px]">
                      {/* Page 1: Owner & Location Information */}
                      {currentPage === 1 && (
                        <div className="space-y-6">
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">👤</span> Basic Property
                              Information
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <span className="text-sm text-gray-600">
                                  Full Name
                                </span>
                                <p className="font-medium">
                                  {property.ownerName}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">
                                  ID Number
                                </span>
                                <p className="font-medium">
                                  {property.ownerIdNumber}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">
                                  UPI Number
                                </span>
                                <p className="font-medium">
                                  {property.upi || "Not assigned"}
                                </p>
                              </div>

                              <div>
                                <span className="text-sm text-gray-600">
                                  Plot Number
                                </span>
                                <p className="font-medium">
                                  {property.plotNumber || "Not assigned"}
                                </p>
                              </div>

                              <div>
                                <span className="text-sm text-gray-600">
                                  Phone Number
                                </span>
                                <p className="font-medium">
                                  {property.phone || "Not provided"}
                                </p>
                              </div>
                            </div>
                          </section>

                          <section className="mt-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📋</span> Assignment
                              details
                            </h2>

                            {/* {valuationTaskDetails.institutionalId && (
                              <div className="mt-2 mb-4">
                                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Details already saved. Edit and save to
                                  update.
                                </div>
                              </div>
                            )} */}

                            <div className="bg-gray-50 rounded-lg p-4">
                              {/* Valuation Task Details Form */}
                              <section className="mt-6">
                                <div className="bg-gray-50 rounded-lg p-6">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Institutional Client Name */}
                                    <div className="space-y-1">
                                      <label className="text-sm font-medium text-gray-700">
                                        Institutional Client Name *
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setIsClientModalOpen(true)
                                        }
                                        className={`w-full px-3 py-2 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                          formErrors.institutionalId
                                            ? "border-red-300 bg-red-50"
                                            : "border-gray-300 bg-white hover:bg-gray-50"
                                        }`}
                                      >
                                        <div className="flex justify-between items-center">
                                          <span
                                            className={
                                              valuationTaskDetails.institutionalId
                                                ? "text-gray-900"
                                                : "text-gray-500"
                                            }
                                          >
                                            {valuationTaskDetails.institutionalId
                                              ? isClientsLoaded &&
                                                institutionalClients.find(
                                                  (c) =>
                                                    c.id ===
                                                    valuationTaskDetails.institutionalId,
                                                )
                                                ? `${
                                                    institutionalClients.find(
                                                      (c) =>
                                                        c.id ===
                                                        valuationTaskDetails.institutionalId,
                                                    )?.firstName || ""
                                                  } ${
                                                    institutionalClients.find(
                                                      (c) =>
                                                        c.id ===
                                                        valuationTaskDetails.institutionalId,
                                                    )?.lastName || ""
                                                  }`
                                                : "Client ID: " +
                                                  valuationTaskDetails.institutionalId
                                              : "Select Institutional Client"}
                                          </span>
                                          <UserPlus className="h-4 w-4 text-gray-400" />
                                        </div>
                                      </button>
                                      {formErrors.institutionalId && (
                                        <p className="text-red-600 text-xs mt-1">
                                          {formErrors.institutionalId}
                                        </p>
                                      )}
                                    </div>

                                    {/* Date of Valuation/Assignment */}
                                    <div className="space-y-1">
                                      <label
                                        htmlFor="valuationDate"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        Date of Valuation/Assignment *
                                      </label>
                                      <input
                                        type="date"
                                        id="valuationAssignmentDate"
                                        value={
                                          valuationTaskDetails.valuationAssignmentDate
                                            ? formatDateForInput(
                                                valuationTaskDetails.valuationAssignmentDate,
                                              )
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const dateValue = e.target.value
                                            ? new Date(e.target.value)
                                            : new Date();
                                          handleTaskDetailChange(
                                            "valuationAssignmentDate",
                                            dateValue,
                                          );
                                        }}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                          formErrors.valuationAssignmentDate
                                            ? "border-red-300 bg-red-50"
                                            : "border-gray-300"
                                        }`}
                                        required
                                      />
                                      {formErrors.valuationAssignmentDate && (
                                        <p className="text-red-600 text-xs mt-1">
                                          {formErrors.valuationAssignmentDate}
                                        </p>
                                      )}
                                    </div>

                                    {/* Professional Certification */}
                                    <div className="space-y-1">
                                      <label
                                        htmlFor="professionalCert"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        Professional Certification *
                                      </label>
                                      <input
                                        type="text"
                                        id="professionalCert"
                                        value={
                                          valuationTaskDetails.professionalCert ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleTaskDetailChange(
                                            "professionalCert",
                                            e.target.value,
                                          )
                                        }
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                          formErrors.professionalCert
                                            ? "border-red-300 bg-red-50"
                                            : "border-gray-300"
                                        }`}
                                        placeholder="e.g., RICS, KISM, etc."
                                        required
                                      />
                                      {formErrors.professionalCert && (
                                        <p className="text-red-600 text-xs mt-1">
                                          {formErrors.professionalCert}
                                        </p>
                                      )}
                                    </div>

                                    {/* Principal Valuer */}
                                    <div className="space-y-1">
                                      <label
                                        htmlFor="principalValuer"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        Principal Valuer *
                                      </label>
                                      <input
                                        type="text"
                                        id="principalValuer"
                                        value={
                                          valuationTaskDetails.principalValuer ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleTaskDetailChange(
                                            "principalValuer",
                                            e.target.value,
                                          )
                                        }
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                          formErrors.principalValuer
                                            ? "border-red-300 bg-red-50"
                                            : "border-gray-300"
                                        }`}
                                        placeholder="Full name of principal valuer"
                                        required
                                      />
                                      {formErrors.principalValuer && (
                                        <p className="text-red-600 text-xs mt-1">
                                          {formErrors.principalValuer}
                                        </p>
                                      )}
                                    </div>

                                    {/* Assistant Valuer */}
                                    <div className="space-y-1">
                                      <label
                                        htmlFor="assistantValuer"
                                        className="text-sm text-gray-600"
                                      >
                                        Assistant Valuer
                                      </label>
                                      <input
                                        type="text"
                                        id="assistantValuer"
                                        value={
                                          valuationTaskDetails.assistantValuer ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleTaskDetailChange(
                                            "assistantValuer",
                                            e.target.value,
                                          )
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Full name of assistant valuer"
                                      />
                                    </div>

                                    {/* Regulatory Body */}
                                    <div className="space-y-1">
                                      <label
                                        htmlFor="regulatoryBody"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        Regulatory Body *
                                      </label>
                                      <input
                                        type="text"
                                        id="regulatoryBody"
                                        value={
                                          valuationTaskDetails.regulatoryBody ||
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleTaskDetailChange(
                                            "regulatoryBody",
                                            e.target.value,
                                          )
                                        }
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                                          formErrors.regulatoryBody
                                            ? "border-red-300 bg-red-50"
                                            : "border-gray-300"
                                        }`}
                                        placeholder="e.g., Valuers Registration Board"
                                        required
                                      />
                                      {formErrors.regulatoryBody && (
                                        <p className="text-red-600 text-xs mt-1">
                                          {formErrors.regulatoryBody}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="mt-6 flex justify-end">
                                    <button
                                      type="button"
                                      onClick={handleSaveValuationDetails}
                                      disabled={isSavingDetails}
                                      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isSavingDetails ? (
                                        <>
                                          <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            ></circle>
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                          </svg>
                                          Saving...
                                        </>
                                      ) : (
                                        "Save Details"
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </section>

                              {/* Institutional Client Selection Modal */}
                              {isClientModalOpen && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4">
                                  <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                                    <div className="flex justify-between items-center mb-4">
                                      <h3 className="text-lg font-medium text-gray-900">
                                        Select Institutional Client
                                      </h3>
                                      <button
                                        onClick={() => {
                                          setIsClientModalOpen(false);
                                          setClientSearchQuery("");
                                        }}
                                        className="text-gray-400 hover:text-gray-500"
                                      >
                                        <X className="h-6 w-6" />
                                      </button>
                                    </div>

                                    <div className="mb-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Search Client
                                      </label>
                                      <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500"
                                        placeholder="Search by name..."
                                        value={clientSearchQuery}
                                        onChange={(e) =>
                                          setClientSearchQuery(e.target.value)
                                        }
                                      />
                                    </div>

                                    <div className="max-h-64 overflow-y-auto mb-4">
                                      {filteredClients.length > 0 ? (
                                        <ul className="border border-gray-300 rounded-md divide-y divide-gray-200">
                                          {filteredClients.map((client) => (
                                            <li
                                              key={client.id}
                                              className={`px-4 py-3 cursor-pointer hover:bg-indigo-50 ${
                                                valuationTaskDetails.institutionalId ===
                                                client.id
                                                  ? "bg-indigo-100"
                                                  : ""
                                              }`}
                                              onClick={() => {
                                                handleTaskDetailChange(
                                                  "institutionalId",
                                                  client.id,
                                                );
                                                setIsClientModalOpen(false);
                                                setClientSearchQuery("");
                                              }}
                                            >
                                              <div className="font-medium">
                                                {client.firstName}{" "}
                                                {client.lastName}
                                              </div>
                                              {client.email && (
                                                <div className="text-xs text-gray-500">
                                                  {client.email}
                                                </div>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        <p className="text-sm text-gray-500 text-center py-4">
                                          No institutional clients found
                                        </p>
                                      )}
                                    </div>

                                    <div className="flex justify-end space-x-3">
                                      <button
                                        onClick={() => {
                                          setIsClientModalOpen(false);
                                          setClientSearchQuery("");
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </section>
                          <TimeTracker property={property} />
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📍</span> Location
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                              {isUganda ? (
                                <>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Country
                                    </span>
                                    <p className="font-medium">Uganda</p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Administrative Region
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.region ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.subRegion && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Sub-region
                                      </span>
                                      <p className="font-medium">
                                        {property.location.subRegion}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      District
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.district ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      County
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.county ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Subcounty
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.subcounty ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Parish
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.parish ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Village
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.village ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.streetAddress && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Address
                                      </span>
                                      <p className="font-medium">
                                        {property.location.streetAddress}
                                      </p>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Country
                                    </span>
                                    <p className="font-medium">Rwanda</p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Province
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.province ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      District
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.district ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Sector
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.sector ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Cell
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.cell ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">
                                      Village
                                    </span>
                                    <p className="font-medium">
                                      {property.location?.village ||
                                        "Not specified"}
                                    </p>
                                  </div>
                                  {property.location?.streetAddress && (
                                    <div>
                                      <span className="text-sm text-gray-600">
                                        Address
                                      </span>
                                      <p className="font-medium">
                                        {property.location.streetAddress}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </section>
                          <section>
                            {property.location?.coordinates && (
                              <div className="bg-gray-50 rounded-lg p-4 flex">
                                {/* // <div className="md:col-span-2"> */}
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                  <span className=""></span>🌍 Coordinates
                                </h2>
                                <p className="font-medium ml-52">
                                  {property.location.coordinates.latitude},{" "}
                                  {property.location.coordinates.longitude}
                                </p>
                              </div>
                            )}
                            {/* {property.location?.streetAddress && (
                      <div className="md:col-span-2">
                        <span className="text-sm text-gray-600">
                          Address
                        </span>
                        <p className="font-medium">
                          {property.location.streetAddress}
                        </p>
                      </div> */}
                            {/* )} */}
                          </section>
                          {/* Valuation Parameters */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📐</span> Valuation
                              Parameters
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Land Use */}
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Land Use
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.landUse || "N/A"}
                                </p>
                              </div>
                              {/* Slope Category */}
                              <div>
                                <label className="text-sm font-medium text-gray-700">
                                  Slope Category
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.slopeCategory || "N/A"}
                                </p>
                              </div>
                              {/* Land Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Land Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.landParcel?.area || 0}
                                </p>
                              </div>
                              {/* Land Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Land Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.landRate || 0}
                                </p>
                              </div>
                              {/* Land Value */}
                              {/* Main House Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Main House Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.mainHouseArea ||
                                    0}
                                </p>
                              </div>
                              {/* Main House Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Main House Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.mainHouseRate ||
                                    0}
                                </p>
                              </div>
                              {/* Annex Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Annex House Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.annexHouseArea ||
                                    0}
                                </p>
                              </div>
                              {/* Annex Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Annex Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.annexRate || 0}
                                </p>
                              </div>
                              {/* Fence Length */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Fence Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.fenceLength || 0}
                                </p>
                              </div>
                              {/* Fence Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Fence Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.fenceRate || 0}
                                </p>
                              </div>
                              {/* Retaining Walls */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Retaining Walls Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.retainingWallsLength || 0}
                                </p>
                              </div>
                              {/* Retaining Wall Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Retaining Wall Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.retainingWallRate || 0}
                                </p>
                              </div>
                              {/* Entrance Gate Length */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Entrance Gate Length (m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.entranceGateLength || 0}
                                </p>
                              </div>
                              {/* Gate Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Entrance Gate Rate (per m)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.entranceGateRate || 0}
                                </p>
                              </div>
                              {/* Paved Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Paved Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.pavedArea || 0}
                                </p>
                              </div>
                              {/* Paved Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Paved Area Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.pavedAreaRate ||
                                    0}
                                </p>
                              </div>
                              {/* Septic Tank */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Septic Tank Number
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.septicTankNumber || 0}
                                </p>
                              </div>
                              {/* Septic Tank Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Septic Tank Value (per unit)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.septicTankValue ||
                                    0}
                                </p>
                              </div>
                              {/* Garden Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Garden Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.gardenArea || 0}
                                </p>
                              </div>
                              {/* Garden Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Garden Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.gardenRate || 0}
                                </p>
                              </div>
                              {/* Verandah Area */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Verandah Area (m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.verandahArea || 0}
                                </p>
                              </div>
                              {/* Verandah Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Verandah Rate (per m²)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails?.verandahRate || 0}
                                </p>
                              </div>
                              {/* Landscaping Value */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Landscaping Value ({currency})
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.landscapingValue || 0}
                                </p>
                              </div>
                              {/* Forced Sale Discount */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Forced Sale Discount (%)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.forcedSaleDiscount || 0}
                                </p>
                              </div>
                              {/* Depreciation Rate */}
                              <div>
                                <label className="text-sm text-gray-600">
                                  Depreciation Rate (%)
                                </label>
                                <p className="font-medium">
                                  {property.valuationDetails
                                    ?.depreciationRate || 0}
                                </p>
                              </div>
                            </div>
                          </section>
                        </div>
                      )}

                      {/* Page 3: Building Components */}
                      {currentPage === 2 &&
                        property.buildingComponents &&
                        property.valuationDetails && (
                          <div className="space-y-6">
                            {/* Amenities */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">✨</span> Amenities
                              </h2>
                              {property.amenitiesData && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {Object.entries(property.amenitiesData).map(
                                      ([key, value]) => (
                                        <div
                                          key={key}
                                          className="flex items-center space-x-2"
                                        >
                                          <div
                                            className={`w-3 h-3 rounded-full ${
                                              value
                                                ? "bg-green-500"
                                                : "bg-gray-300"
                                            }`}
                                          />
                                          <span className="text-sm capitalize">
                                            {key
                                              .replace("has", "")
                                              .replace(/([A-Z])/g, " $1")
                                              .trim()}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </section>
                            {/* Building Components */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">🔧</span> Building
                                Components
                              </h2>
                              <div className="space-y-3">
                                {[
                                  {
                                    label: "Foundation Description",
                                    value:
                                      property.buildingComponents
                                        ?.outsideKitchenDescription,
                                  },
                                  {
                                    label: "Roof Description",
                                    value:
                                      property.buildingComponents
                                        ?.waterTankDescription,
                                  },
                                  {
                                    label: " Accomodation Description",
                                    value:
                                      property.buildingComponents
                                        ?.landscapingDescription,
                                  },
                                  {
                                    label: "Walls Description",
                                    value:
                                      property.buildingComponents
                                        ?.soilDrainageDescription,
                                  },
                                  {
                                    label: "Doors & Windows Description",
                                    value:
                                      property.buildingComponents
                                        ?.pvcPipesDescription,
                                  },
                                  {
                                    label: "Floor Description",
                                    value:
                                      property.buildingComponents
                                        ?.washTapsDescription,
                                  },
                                  {
                                    label: "Ceiling Description",
                                    value:
                                      property.buildingComponents
                                        ?.fixturesFittingsDescription,
                                  },
                                ]
                                  .filter((item) => item.value)
                                  .map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-gray-50 rounded-lg p-4"
                                    >
                                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        {item.label}
                                      </h3>
                                      <p className="text-sm text-gray-600 break-words">
                                        {item.value}
                                      </p>
                                    </div>
                                  ))}
                              </div>
                            </section>
                            {/* Building Description */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">🏗️</span> Building
                                Description
                              </h2>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Roof Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.roofCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Walls Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.wallsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Windows Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.windowsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Doors Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.doorsCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Floor Condition
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.floorCondition ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Roof Type
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.roofType || "N/A"}
                                  </p>
                                </div>
                                <div className="space-y-3">
                                  <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                      Bedrooms
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {property.mainBuilding?.bedrooms || 0}
                                    </p>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-4">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                      Bathrooms
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {property.mainBuilding?.bathrooms || 0}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </section>
                            {/* Adjustments & Variables */}
                            <section>
                              <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                <span className="mr-2">📊</span> Adjustments &
                                Variables
                              </h2>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Year of Construction
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property?.yearOfConstruction || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Effective Age (years)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {new Date().getFullYear() -
                                      (property?.yearOfConstruction || 0)}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Annual Depreciation Rate (%)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.annualDepreciationRate || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Unit Price
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.unitPrice || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Constructed Area (m²)
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.mainBuilding?.constructedArea ||
                                      0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Original Cost of Construction
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.originalCostOfConstruction || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    Maintenance Coefficient
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.maintenanceCoefficient || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    {`Depreciated Value (${currency})`}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails
                                      ?.depreciatedValue || 0}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                    {`Present Value (${currency})`}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    {property.valuationDetails?.presentValue ||
                                      0}
                                  </p>
                                </div>
                              </div>
                            </section>
                          </div>
                        )}

                      {/* Page 4: Valuation Measurements & Rates */}
                      {currentPage === 3 && property.valuationDetails && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {propertyImages.map((image, index) => (
                              <div
                                key={index}
                                className="bg-white rounded-lg shadow-sm border p-2 cursor-pointer"
                                onClick={() => setZoomImage(image.url)}
                              >
                                <div className="aspect-square w-full rounded overflow-hidden">
                                  <img
                                    src={image.url}
                                    alt={image.label}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <p className="text-center text-gray-700 mt-2 text-sm font-medium">
                                  {image.label}
                                </p>
                              </div>
                            ))}
                          </div>
                          {/* Fullscreen Zoom Modal */}
                          {zoomImage && (
                            <div
                              className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                              onClick={() => setZoomImage(null)}
                            >
                              <img
                                src={zoomImage}
                                alt="Zoomed"
                                className="max-w-full max-h-full object-contain"
                              />
                              <button
                                className="absolute top-4 right-4 text-white text-4xl font-bold"
                                onClick={() => setZoomImage(null)}
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Page 5: Financial Analysis & Summary */}
                      {currentPage === 4 && property.valuationDetails && (
                        <>
                          {/* Property location on map*/}
                          {property.mainBuilding && (
                            <div className="mb-6">
                              <h3 className="text-base font-medium text-gray-800 mb-3">
                                🗺️ Property Location
                              </h3>
                              <PropertyMap
                                Upi={property.upi || ""}
                                location={[
                                  property?.location?.coordinates?.latitude ||
                                    1.88596,
                                  property?.location?.coordinates?.longitude ||
                                    30.129675,
                                ]}
                              />
                              <div className="grid grid-cols-1 gap-4 md:gap-8 mt-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Google Maps View
                                  </h4>
                                  <iframe
                                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.979567234864!2d${
                                      property?.location?.coordinates
                                        ?.longitude || 0
                                    }!3d${
                                      property?.location?.coordinates
                                        ?.latitude || 0
                                    }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjM6MTM0!5e0!3m2!1sen!2sng!4v1680879000000!5m2!1sen!2sng`}
                                    width="100%"
                                    height="400"
                                    style={{ border: 0, borderRadius: "8px" }}
                                    allowFullScreen={false}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                                    Kigali City Masterplan
                                  </h4>
                                  <iframe
                                    src="https://masterplan.kigalicity.gov.rw/portal/apps/webappviewer/index.html?id=fdd2e30cbc15401d9daee5f68982d755"
                                    width="100%"
                                    height="400"
                                    style={{ border: 0, borderRadius: "8px" }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Kigali City Masterplan"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Property Details */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📋</span> Property Details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Area (sqft)
                                </h3>
                                <p className="text-lg font-medium text-gray-900">
                                  {property?.area}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Age (years)
                                </h3>
                                <p className="text-lg font-medium text-gray-900">
                                  {property?.age || 0}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Condition (1-10)
                                </h3>
                                <div className="flex items-center">
                                  <p className="text-lg font-medium text-gray-900 mr-3">
                                    {(() => {
                                      const conditionMap: Record<
                                        string,
                                        number
                                      > = {
                                        Excellent: 10,
                                        Good: 7,
                                        Average: 5,
                                        Poor: 2,
                                      };
                                      return property.condition;
                                    })()}
                                  </p>
                                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-green-500"
                                      style={{
                                        width: `${
                                          ([
                                            "Excellent",
                                            "Good",
                                            "Average",
                                            "Poor",
                                          ].indexOf(
                                            property.mainBuilding
                                              ?.maintenanceCondition || "",
                                          ) +
                                            1) *
                                          25
                                        }%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                  Zoning
                                </h3>
                                <p className="text-gray-600">
                                  {property.zoning || "N/A"}
                                </p>
                              </div>
                            </div>
                          </section>
                          {/* Comparative Analysis */}
                          <section>
                            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                              <span className="mr-2">📊</span> Comparative
                              Analysis
                            </h2>
                            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Property
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Location
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Area
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Price
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                      Adjustments
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      Subject Property
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {property.location?.district},{" "}
                                      {property.location?.sector}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {property?.area || 0} m²
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      {currency}{" "}
                                      {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      N/A
                                    </td>
                                  </tr>
                                  <tr className="bg-blue-50">
                                    <td
                                      className="px-4 py-3 text-sm font-medium text-blue-900"
                                      colSpan={3}
                                    >
                                      Comparable Value
                                    </td>
                                    <td className="px-4 py-3 text-lg font-bold text-blue-900">
                                      {currency}{" "}
                                      {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-600">
                                      Market Adjusted
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-4 bg-gray-50 rounded-lg p-4">
                              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                Summary
                              </h3>
                              <p className="text-sm text-gray-600">
                                Based on property data, the comparable Value is
                                estimated at{" "}
                                <span className="font-semibold">
                                  {currency}{" "}
                                  {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                                </span>
                                .
                              </p>
                            </div>
                          </section>
                        </>
                      )}
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-8 pt-6 border-t">
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition"
                      >
                        <ChevronLeft size={16} className="mr-1" />
                        Previous
                      </button>
                      <div className="flex items-center space-x-2">
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`w-8 h-8 rounded-full text-sm font-medium transition ${
                              currentPage === i + 1
                                ? "bg-blue-600 text-white"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition"
                      >
                        Next
                        <ChevronRight size={16} className="ml-1" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 space-y-6">
                    <ValuationSidebarSuperVisor
                      property={property}
                      propertyId={propertyId}
                      mode="supervisor"
                      onValuationComplete={() => {}}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {errorMessage}
            </div>
          )}

          {filter === "graphics" && (
            <div className="space-x-3 mr-5">
              <PropertyValuationDashboard property={property} />
              <ValuationVisualization property={property} />
              {/* <CMACalculator /> */}
              <PropertyDashboard property={property} />
              <PropertyInfographic property={property} />

              <ValuationTimeline property={property} />
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Lightbox Modal */}
          {isLightboxOpen && hasImages && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
              <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
                {/* Close button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-10 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>

                {/* Zoom controls */}
                <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="text-white text-sm">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 3}
                    className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
                  >
                    <Plus size={20} />
                  </button>
                </div>

                {/* Navigation buttons */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>

                {/* Main image container - no scroll */}
                <div className="w-full h-full flex items-center justify-center p-4">
                  <div className="max-w-full max-h-full flex items-center justify-center">
                    <img
                      src={propertyImages[selectedImageIndex].url}
                      alt={propertyImages[selectedImageIndex].label}
                      className="max-w-full max-h-full object-contain transition-transform duration-200"
                      style={{ transform: `scale(${zoomLevel})` }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          "https://via.placeholder.com/800x600/cccccc/969696?text=Image+Not+Found";
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronRight size={24} />
                </button>

                {/* Image label */}
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium text-center px-4">
                  {propertyImages[selectedImageIndex].label}
                </div>

                {/* Image counter */}
                <div className="absolute top-4 right-16 text-white text-sm">
                  {selectedImageIndex + 1} / {propertyImages.length}
                </div>

                {/* Thumbnails - positioned above */}
                <div className="absolute bottom-4 left-0 right-0 px-4">
                  <div className="flex overflow-x-auto space-x-2 justify-center py-2 max-w-full">
                    {propertyImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setZoomLevel(1);
                        }}
                        className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                          index === selectedImageIndex
                            ? "border-blue-500"
                            : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={image.label}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="ml-32 h-16"></div>
    </div>
  );
};

export default PropertyDetails;
